/**
 * Nosto-CompleteLook.js
 *
 * Responsabilidades:
 * - consumir recomendaciones normalizadas;
 * - soportar productos manuales desde metafield;
 * - renderizar cards compactos mediante NostoProductCard;
 * - reutilizar el grid-slider nativo del theme;
 * - mantener attribution de Nosto;
 * - administrar el drawer "Ver todo".
 *
 * No consulta directamente a Nosto.
 * No construye el HTML interno del product card.
 */
(function initializeNostoCompleteLook(global) {
    "use strict";

    class NostoCompleteLook extends HTMLElement {
        constructor() {
            super();

            this.cards = [];

            this.allRecommendedProducts = [];
            this.displayedRecommendedProducts = [];

            this.viewService = null;

            this.hasRendered = false;

            this.slideObserver = null;

            this.drawerRendered = false;
            this.drawerItemControllers = [];

            this.drawerTriggerButton = null;
            this.isDrawerClosing = false;

            this.handleRecommendationsLoaded =
                this.handleRecommendationsLoaded.bind(this);

            this.handleRecommendationsError =
                this.handleRecommendationsError.bind(this);

            this.handleClick =
                this.handleClick.bind(this);

            this.handleDrawerCancel =
                this.handleDrawerCancel.bind(this);

            this.handleProductAdded =
                this.handleProductAdded.bind(this);

            this.recommendationContext = {
                source: "",
                placementId: "",
                resultId: "",
                divId: "",
            };
        }

        connectedCallback() {
            this.addEventListener(
                "nosto:recommendations:loaded",
                this.handleRecommendationsLoaded
            );

            this.addEventListener(
                "nosto:recommendations:error",
                this.handleRecommendationsError
            );

            this.addEventListener(
                "click",
                this.handleClick
            );

            document.addEventListener(
                "theme:product:add",
                this.handleProductAdded
            );

            const drawer =
                this.querySelector(
                    "[data-complete-look-drawer]"
                );

            drawer?.addEventListener(
                "cancel",
                this.handleDrawerCancel
            );

            this.initializeViewService();

            this.initializeManualProducts();
        }

        disconnectedCallback() {
            this.removeEventListener(
                "nosto:recommendations:loaded",
                this.handleRecommendationsLoaded
            );

            this.removeEventListener(
                "nosto:recommendations:error",
                this.handleRecommendationsError
            );

            this.removeEventListener(
                "click",
                this.handleClick
            );

            const drawer =
                this.querySelector(
                    "[data-complete-look-drawer]"
                );

            drawer?.removeEventListener(
                "cancel",
                this.handleDrawerCancel
            );

            document.removeEventListener(
                "theme:product:add",
                this.handleProductAdded
            );

            this.slideObserver
                ?.disconnect();

            this.slideObserver = null;

            this.destroyCards();
        }

        /**
         * Inicializa el servicio de vistas utilizado por
         * Complete Look.
         *
         * El card principal ahora utiliza:
         *
         * ?view=nosto-card-compact
         */
        initializeViewService() {
            if (
                typeof global.NostoProductViewService !==
                "function"
            ) {
                console.error(
                    "[NostoCompleteLook] NostoProductViewService no está disponible."
                );

                return;
            }

            this.viewService =
                new global.NostoProductViewService({
                    cardView:
                        "nosto-card-compact",

                    quickBuyView:
                        "nosto-quick-buy",

                    concurrency: 4,

                    cache: true,

                    debug:
                        global.NOSTO_DEBUG === true,
                });
        }

        initializeDots() {
            const slider =
                this.querySelector(
                    "[data-complete-look-products]"
                );

            const sourceDots =
                this.querySelector(
                    "[data-complete-look-dots]"
                );

            if (
                !slider ||
                !sourceDots
            ) {
                return;
            }

            const slides =
                Array.from(
                    slider.querySelectorAll(
                        "[data-complete-look-slide]"
                    )
                );

            sourceDots.replaceChildren();

            if (slides.length <= 1) {
                sourceDots.hidden = true;
                return;
            }

            sourceDots.hidden = true;

            slides.forEach(
                (_, index) => {
                    const dot =
                        document.createElement(
                            "button"
                        );

                    dot.type = "button";

                    dot.className =
                        "nosto-complete-look__dot";

                    dot.dataset.completeLookDot =
                        "";

                    dot.dataset.index =
                        String(index);

                    dot.setAttribute(
                        "aria-label",
                        `Ir al producto ${index + 1}`
                    );

                    if (index === 0) {
                        dot.classList.add(
                            "is-active"
                        );
                    }

                    dot.addEventListener(
                        "click",
                        () => {
                            slider.scrollTo({
                                left:
                                    slides[index]
                                        .offsetLeft,

                                behavior:
                                    "smooth",
                            });
                        }
                    );

                    sourceDots.appendChild(
                        dot
                    );
                }
            );

            /*
             * grid-slider clona el contenido central.
             * Lo reinicializamos después de construir
             * los dots para que clone el estado correcto.
             */
        }

        async handleProductAdded(event) {
            const button =
                event.detail?.button;

            if (
                !(button instanceof Element)
            ) {
                return;
            }

            if (
                !this.contains(button)
            ) {
                return;
            }

            if (
                !button.matches(
                    "[data-complete-look-add-to-cart]"
                )
            ) {
                return;
            }

            const item =
                button.closest(
                    "[data-nosto-complete-look-item]"
                );

            if (item) {
                await this.recordDrawerAddToCart(
                    item
                );
            }

            this.closeDrawer();
        }

        async recordDrawerAddToCart(item) {
            if (
                this.recommendationContext
                    ?.source !== "nosto"
            ) {
                return false;
            }

            const productId =
                String(
                    item.dataset
                        .activeProductId ||
                    item.dataset
                        .productId ||
                    ""
                ).trim();

            const placementId =
                String(
                    this.recommendationContext
                        ?.placementId ||
                    ""
                ).trim();

            if (
                !productId ||
                !placementId
            ) {
                return false;
            }

            if (
                typeof global.nostojs !==
                "function"
            ) {
                console.warn(
                    "[NostoCompleteLook] nostojs no está disponible para ATC attribution."
                );

                return false;
            }

            try {
                await new Promise(
                    (resolve, reject) => {
                        global.nostojs(
                            (api) => {
                                try {
                                    api
                                        .recordAttribution({
                                            type:
                                                "cp",

                                            target:
                                                productId,

                                            ref:
                                                placementId,

                                            refType:
                                                "api",
                                        })
                                        .done();

                                    resolve();

                                } catch (error) {
                                    reject(
                                        error
                                    );
                                }
                            }
                        );
                    }
                );

                return true;

            } catch (error) {
                console.error(
                    "[NostoCompleteLook] No fue posible registrar ATC attribution.",
                    {
                        productId,
                        placementId,
                        error,
                    }
                );

                return false;
            }
        }

        initializeSlideObserver() {
            this.slideObserver
                ?.disconnect();

            const slider =
                this.querySelector(
                    "[data-complete-look-products]"
                );

            if (!slider) {
                return;
            }

            const slides =
                Array.from(
                    slider.querySelectorAll(
                        "[data-complete-look-slide]"
                    )
                );

            if (!slides.length) {
                return;
            }

            this.slideObserver =
                new IntersectionObserver(
                    (entries) => {
                        const visibleEntry =
                            entries
                                .filter(
                                    (entry) =>
                                        entry.isIntersecting
                                )
                                .sort(
                                    (a, b) =>
                                        b.intersectionRatio -
                                        a.intersectionRatio
                                )[0];

                        if (!visibleEntry) {
                            return;
                        }

                        const activeIndex =
                            slides.indexOf(
                                visibleEntry.target
                            );

                        if (activeIndex < 0) {
                            return;
                        }

                        this.updateDots(
                            activeIndex
                        );
                    },
                    {
                        root:
                            slider,

                        threshold: [
                            0.5,
                            0.75,
                            0.9,
                        ],
                    }
                );

            slides.forEach(
                (slide) => {
                    this.slideObserver.observe(
                        slide
                    );
                }
            );
        }

        updateDots(activeIndex) {
            this.querySelectorAll(
                "[data-complete-look-dot]"
            ).forEach(
                (dot) => {
                    dot.classList.toggle(
                        "is-active",
                        Number(
                            dot.dataset.index
                        ) === activeIndex
                    );
                }
            );

            /*
             * GridSlider clona el contenido central,
             * por lo que actualizamos también el clon.
             */
            this.querySelectorAll(
                "[data-grid-slider-center-content-rendered] [data-complete-look-dot]"
            ).forEach(
                (dot) => {
                    dot.classList.toggle(
                        "is-active",
                        Number(
                            dot.dataset.index
                        ) === activeIndex
                    );
                }
            );
        }

        /**
         * Inicializa productos definidos manualmente mediante:
         *
         * product.metafields.custom.completa_tu_look
         */
        async initializeManualProducts() {
            const manualProductsElement =
                this.querySelector(
                    "[data-complete-look-manual-products]"
                );

            if (!manualProductsElement) {
                return;
            }

            let products = [];

            try {
                products = JSON.parse(
                    manualProductsElement.textContent ||
                    "[]"
                );
            } catch (error) {
                console.error(
                    "[NostoCompleteLook] No fue posible leer custom.completa_tu_look.",
                    error
                );

                return;
            }

            products = products
                .map((product) => ({
                    productId: String(
                        product.productId || ""
                    ).trim(),

                    handle: String(
                        product.handle || ""
                    ).trim(),

                    url: String(
                        product.url || ""
                    ).trim(),
                }))
                .filter(
                    (product) =>
                        product.productId &&
                        product.handle
                );

            if (!products.length) {
                return;
            }

            const visibleProducts =
                Math.max(
                    1,
                    Number(
                        this.dataset.visibleProducts ||
                        3
                    ) || 3
                );

            this.allRecommendedProducts =
                products;

            this.displayedRecommendedProducts =
                products.slice(
                    0,
                    visibleProducts
                );

            this.updateViewAllVisibility();

            this.recommendationContext = {
                source: "metafield",
                placementId: "",
                resultId: "",
                divId: "",
            };

            /*
             * Los productos del metafield no tienen:
             *
             * - placementId
             * - resultId
             * - divId
             *
             * porque no provienen de una impresión
             * de recomendaciones de Nosto.
             */
            await this.renderCompleteLook(
                this.displayedRecommendedProducts,
                {
                    source:
                        "metafield",
                }
            );

            this.dispatchEvent(
                new CustomEvent(
                    "nosto:complete-look:rendered",
                    {
                        bubbles: true,

                        detail: {
                            source:
                                "metafield",

                            displayedProducts:
                                this.displayedRecommendedProducts,

                            allProducts:
                                this.allRecommendedProducts,

                            cards:
                                this.cards,
                        },
                    }
                )
            );
        }

        /**
         * Render principal.
         */
        async renderCompleteLook(
            products,
            recommendationContext = {}
        ) {
            if (
                !Array.isArray(products) ||
                products.length === 0
            ) {
                this.showEmptyState();
                return;
            }

            /*
             * El componente debe estar visible antes de
             * medir e inicializar el grid-slider.
             */
            this.hidden = false;

            this.removeAttribute(
                "data-empty"
            );

            this.classList.add(
                "is-rendering"
            );

            try {
                await this.waitForNextFrame();

                await this.renderProducts(
                    products,
                    recommendationContext
                );

                const productsContainer =
                    this.querySelector(
                        "[data-complete-look-products]"
                    );

                await this.waitForImages(
                    productsContainer
                );

                await this.waitForNextFrame();

                /*
                 * Ahora inicializamos el carrusel reusable
                 * del theme.
                 */
                this.initializeDots();

                this.initializeGridSlider();

                this.initializeSlideObserver();

                this.hasRendered = true;

                this.dataset.loaded =
                    "true";
            } finally {
                this.classList.remove(
                    "is-rendering"
                );
            }
        }

        /**
         * Recomendaciones provenientes de Nosto.
         */
        async handleRecommendationsLoaded(
            event
        ) {
            const recommendationsElement =
                event.target.closest(
                    "nosto-recommendations"
                );

            /*
             * Ignorar eventos pertenecientes a otro
             * componente Nosto de la página.
             */
            if (
                !recommendationsElement ||
                !this.contains(
                    recommendationsElement
                )
            ) {
                return;
            }

            /*
             * Si Complete Look está utilizando el
             * metafield, no debe reaccionar a Nosto.
             */
            if (
                this.dataset.source ===
                "metafield"
            ) {
                return;
            }

            const detail =
                event.detail || {};

            this.recommendationContext = {
                source: "nosto",

                placementId:
                    String(
                        detail.placementId ||
                        ""
                    ).trim(),

                resultId:
                    String(
                        detail.resultId ||
                        ""
                    ).trim(),

                divId:
                    String(
                        detail.divId ||
                        ""
                    ).trim(),
            };

            this.allRecommendedProducts =
                Array.isArray(
                    detail.allRecommendedProducts
                )
                    ? detail.allRecommendedProducts
                    : [];

            this.displayedRecommendedProducts =
                Array.isArray(
                    detail.recommendedProducts
                )
                    ? detail.recommendedProducts
                    : [];

            if (
                this.displayedRecommendedProducts
                    .length === 0
            ) {
                this.showEmptyState();
                return;
            }

            this.updateViewAllVisibility();

            await this.renderCompleteLook(
                this.displayedRecommendedProducts,
                {
                    source:
                        "nosto",

                    placementId:
                        detail.placementId,

                    resultId:
                        detail.resultId,

                    divId:
                        detail.divId,
                }
            );

            this.dispatchEvent(
                new CustomEvent(
                    "nosto:complete-look:rendered",
                    {
                        bubbles: true,

                        detail: {
                            source:
                                "nosto",

                            placementId:
                                detail.placementId,

                            resultId:
                                detail.resultId,

                            divId:
                                detail.divId,

                            displayedProducts:
                                this.displayedRecommendedProducts,

                            allProducts:
                                this.allRecommendedProducts,

                            cards:
                                this.cards,
                        },
                    }
                )
            );
        }

        /**
         * Error proveniente de nosto-recommendations.
         */
        handleRecommendationsError(
            event
        ) {
            if (
                this.dataset.source ===
                "metafield"
            ) {
                return;
            }

            const recommendationsElement =
                event.target.closest(
                    "nosto-recommendations"
                );

            if (
                !recommendationsElement ||
                !this.contains(
                    recommendationsElement
                )
            ) {
                return;
            }

            console.error(
                "[NostoCompleteLook] No fue posible cargar recomendaciones.",
                event.detail?.error
            );

            this.hidden = true;
        }

        /**
         * Renderiza los productos visibles como slides.
         *
         * Cada slide ocupa el 100% del ancho y contiene
         * un NostoProductCard cargado mediante:
         *
         * ?view=nosto-card-compact
         */
        async renderProducts(
            products,
            recommendationContext = {}
        ) {
            const container =
                this.querySelector(
                    "[data-complete-look-products]"
                );

            if (!container) {
                throw new Error(
                    "[NostoCompleteLook] No existe [data-complete-look-products]."
                );
            }

            if (
                !this.viewService ||
                typeof global.NostoProductCard !==
                "function"
            ) {
                throw new Error(
                    "[NostoCompleteLook] Faltan las dependencias para renderizar cards."
                );
            }

            /*
             * Destruimos primero cualquier NostoProductCard
             * anterior antes de reemplazar el DOM.
             */
            this.destroyCards();

            container.replaceChildren();

            const source =
                recommendationContext.source ===
                    "metafield"
                    ? "complete-look-metafield"
                    : "complete-look";

            const slides = [];

            const renderPromises =
                products.map(
                    (
                        recommendedProduct,
                        index
                    ) => {
                        const productId =
                            String(
                                recommendedProduct.productId ||
                                ""
                            ).trim();

                        const handle =
                            String(
                                recommendedProduct.handle ||
                                ""
                            ).trim();

                        if (!handle) {
                            console.warn(
                                "[NostoCompleteLook] Producto recomendado sin handle:",
                                recommendedProduct
                            );

                            slides.push(null);

                            return Promise.resolve(
                                null
                            );
                        }

                        /*
                         * Este elemento es el slide real que
                         * reconoce grid-slider.
                         */
                        const slide =
                            document.createElement(
                                "div"
                            );

                        slide.className =
                            "nosto-complete-look__slide";

                        /*
                         * Contrato con el carrusel del theme.
                         */
                        slide.dataset.gridItem =
                            "";

                        /*
                         * Selector propio de Complete Look.
                         */
                        slide.dataset.completeLookSlide =
                            "";

                        slide.dataset.productId =
                            productId;

                        slide.dataset.productHandle =
                            handle;

                        slide.innerHTML = `
                            <div
                                class="nosto-complete-look__skeleton"
                                aria-hidden="true"
                            ></div>
                        `;

                        container.appendChild(
                            slide
                        );

                        slides.push(
                            slide
                        );

                        /*
                         * NostoProductCard requiere un hit.
                         *
                         * Creamos únicamente la información
                         * mínima que necesita la API actual.
                         */
                        const hit = {
                            id:
                                productId,

                            productId,

                            handle,

                            productHandle:
                                handle,

                            url:
                                recommendedProduct.url ||
                                `/products/${encodeURIComponent(
                                    handle
                                )}`,
                        };

                        const card =
                            new global.NostoProductCard({
                                hit,

                                productId,

                                container:
                                    slide,

                                /*
                                 * El diseño compacto lo define
                                 * la vista Liquid.
                                 *
                                 * No necesitamos introducir
                                 * un nuevo cardType.
                                 */
                                cardType:
                                    "normal",

                                viewName:
                                    "nosto-card-compact",

                                viewService:
                                    this.viewService,

                                source,

                                /*
                                 * Solo productos provenientes
                                 * de Nosto necesitan attribution.
                                 */
                                track:
                                    recommendationContext.source ===
                                        "nosto"
                                        ? "recommendation"
                                        : "",

                                attributionKey:
                                    recommendationContext.resultId ||
                                    "",

                                context: {
                                    source,

                                    position:
                                        index,

                                    cardType:
                                        "normal",

                                    placementId:
                                        recommendationContext.placementId ||
                                        "",

                                    resultId:
                                        recommendationContext.resultId ||
                                        "",

                                    divId:
                                        recommendationContext.divId ||
                                        "",
                                },

                                onError: ({
                                    error,
                                }) => {
                                    console.error(
                                        "[NostoCompleteLook] Error en product card:",
                                        {
                                            recommendedProduct,
                                            error,
                                        }
                                    );
                                },
                            });

                        this.cards.push(
                            card
                        );

                        return card.render();
                    }
                );

            /*
             * Un card con error no debe impedir que
             * aparezcan los demás.
             */
            const results =
                await Promise.allSettled(
                    renderPromises
                );

            /*
             * Eliminamos slides cuyo render falló.
             */
            results.forEach(
                (result, index) => {
                    const slide =
                        slides[index];

                    if (!slide) {
                        return;
                    }

                    const renderedCorrectly =
                        result.status ===
                        "fulfilled" &&
                        result.value;

                    if (
                        !renderedCorrectly
                    ) {
                        slide.remove();
                    }
                }
            );

            await this.waitForNextFrame();

            const renderedCount =
                results.filter(
                    (result) =>
                        result.status ===
                        "fulfilled" &&
                        result.value
                ).length;

            if (
                renderedCount === 0
            ) {
                this.showEmptyState();
            }
        }

        /**
         * Inicializa/reinicializa el grid-slider reusable
         * incluido en theme.js.
         *
         * El componente escucha:
         *
         * theme:grid-slider:init
         *
         * y vuelve a buscar todos sus [data-grid-item].
         */
        initializeGridSlider() {
            const gridSlider =
                this.querySelector(
                    "grid-slider"
                );

            if (!gridSlider) {
                console.warn(
                    "[NostoCompleteLook] No existe <grid-slider>."
                );

                return;
            }

            const slider =
                gridSlider.querySelector(
                    "[data-grid-slider]"
                );

            const slides =
                slider?.querySelectorAll(
                    "[data-grid-item]"
                );

            if (
                !slider ||
                !slides?.length
            ) {
                return;
            }

            /*
             * Importante:
             *
             * Complete Look se conecta inicialmente cuando el
             * contenedor todavía está vacío.
             *
             * Una vez llegaron los productos dinámicos,
             * obligamos al grid-slider a volver a inicializarse.
             */
            gridSlider.dispatchEvent(
                new CustomEvent(
                    "theme:grid-slider:init"
                )
            );
        }

        /**
         * Espera que terminen de cargar las imágenes de
         * los cards compactos antes de medir el carrusel.
         */
        async waitForImages(
            container,
            timeout = 1500
        ) {
            if (!container) {
                return;
            }

            const images =
                Array.from(
                    container.querySelectorAll(
                        ".nosto-product-card-compact img"
                    )
                );

            if (!images.length) {
                return;
            }

            const imagePromises =
                images.map(
                    (image) => {
                        if (
                            image.complete &&
                            image.naturalWidth > 0
                        ) {
                            return Promise.resolve();
                        }

                        return new Promise(
                            (resolve) => {
                                const finish =
                                    () => {
                                        image.removeEventListener(
                                            "load",
                                            finish
                                        );

                                        image.removeEventListener(
                                            "error",
                                            finish
                                        );

                                        resolve();
                                    };

                                image.addEventListener(
                                    "load",
                                    finish,
                                    {
                                        once: true,
                                    }
                                );

                                image.addEventListener(
                                    "error",
                                    finish,
                                    {
                                        once: true,
                                    }
                                );
                            }
                        );
                    }
                );

            const timeoutPromise =
                new Promise(
                    (resolve) => {
                        global.setTimeout(
                            resolve,
                            timeout
                        );
                    }
                );

            await Promise.race([
                Promise.allSettled(
                    imagePromises
                ),

                timeoutPromise,
            ]);
        }

        waitForNextFrame() {
            return new Promise(
                (resolve) => {
                    requestAnimationFrame(
                        () => {
                            requestAnimationFrame(
                                resolve
                            );
                        }
                    );
                }
            );
        }

        /**
         * Botón "Ver todo".
         */
        updateViewAllVisibility() {
            const button =
                this.querySelector(
                    "[data-complete-look-view-all]"
                );

            if (!button) {
                return;
            }

            button.hidden =
                this.allRecommendedProducts
                    .length <=
                this.displayedRecommendedProducts
                    .length;
        }

        /**
         * Delegación general de clicks.
         */
        handleClick(event) {
            const drawer =
                this.querySelector(
                    "[data-complete-look-drawer]"
                );

            /*
             * Cambio de producto/color
             */
            const colorButton =
                event.target.closest(
                    "[data-nosto-complete-look-item] [data-look-color-product]"
                );

            if (
                colorButton &&
                this.contains(colorButton)
            ) {
                event.preventDefault();

                this.selectDrawerColor(
                    colorButton
                );

                return;
            }

            /*
             * Cambio de talla
             */
            const sizeButton =
                event.target.closest(
                    "[data-nosto-complete-look-item] [data-complete-look-size]"
                );

            if (
                sizeButton &&
                this.contains(sizeButton)
            ) {
                event.preventDefault();

                this.selectDrawerSize(
                    sizeButton
                );

                return;
            }

            /*
             * Click en backdrop
             */
            if (
                drawer &&
                event.target === drawer
            ) {
                event.preventDefault();

                this.closeDrawer();

                return;
            }

            const viewAllButton =
                event.target.closest(
                    "[data-complete-look-view-all]"
                );

            if (viewAllButton) {
                event.preventDefault();

                this.openDrawer(
                    viewAllButton
                );

                return;
            }

            const closeButton =
                event.target.closest(
                    "[data-complete-look-drawer-close]"
                );

            if (closeButton) {
                event.preventDefault();

                this.closeDrawer();
            }
        }

        initializeDrawerItem(item) {
            if (!item) {
                return;
            }

            item.productCatalog =
                this.parseDrawerProductCatalog(
                    item
                );

            item.activeProduct =
                item.productCatalog.find(
                    (product) =>
                        String(product.id) ===
                        String(
                            item.dataset.activeProductId ||
                            item.dataset.productId
                        )
                ) ||
                item.productCatalog[0] ||
                null;

            if (!item.activeProduct) {
                return;
            }

            const initialVariantId =
                String(
                    item.dataset.selectedVariantId ||
                    ""
                );

            item.selectedVariant =
                item.activeProduct.variants?.find(
                    (variant) =>
                        String(variant.id) ===
                        initialVariantId
                ) ||
                item.activeProduct.variants?.find(
                    (variant) =>
                        variant.available
                ) ||
                null;

            this.renderDrawerSizes(
                item
            );

            this.updateDrawerItem(
                item
            );
        }


        parseDrawerProductCatalog(item) {
            const element =
                item.querySelector(
                    "[data-look-product-catalog]"
                );

            if (!element) {
                return [];
            }

            try {
                const catalog =
                    JSON.parse(
                        element.textContent ||
                        "[]"
                    );

                return Array.isArray(catalog)
                    ? catalog
                    : [];
            } catch (error) {
                console.error(
                    "[NostoCompleteLook] Catálogo de producto inválido.",
                    error
                );

                return [];
            }
        }


        selectDrawerColor(button) {
            const item =
                button.closest(
                    "[data-nosto-complete-look-item]"
                );

            if (
                !item ||
                !Array.isArray(item.productCatalog)
            ) {
                return;
            }

            const product =
                item.productCatalog.find(
                    (candidate) =>
                        String(candidate.id) ===
                        String(
                            button.dataset.productId
                        )
                );

            if (
                !product ||
                product === item.activeProduct
            ) {
                return;
            }

            item.activeProduct =
                product;

            item.dataset.activeProductId =
                String(product.id);

            item.dataset.productId =
                String(product.id);

            /*
             * Estado visual de swatches
             */
            item.querySelectorAll(
                "[data-look-color-product]"
            ).forEach(
                (candidate) => {
                    const active =
                        candidate === button;

                    candidate.classList.toggle(
                        "is-active",
                        active
                    );

                    candidate.setAttribute(
                        "aria-pressed",
                        String(active)
                    );
                }
            );

            /*
             * Links y título
             */
            const title =
                item.querySelector(
                    "[data-look-item-title]"
                );

            if (title) {
                title.textContent =
                    product.title || "";
            }

            item.querySelectorAll(
                "[data-look-item-link]"
            ).forEach(
                (link) => {
                    if (!product.url) {
                        return;
                    }

                    link.href =
                        this.buildRecommendationUrl(
                            product.url
                        );
                }
            );

            /*
             * Imagen principal del nuevo color
             */
            const image =
                item.querySelector(
                    ".look-product-item__image"
                );

            if (
                image &&
                product.image
            ) {
                image.src =
                    product.image;

                image.removeAttribute(
                    "srcset"
                );
            }

            /*
             * Al cambiar de color/producto elegimos
             * su primera variante disponible.
             */
            item.selectedVariant =
                product.variants?.find(
                    (variant) =>
                        variant.available
                ) ||
                null;

            /*
             * Las tallas pueden cambiar entre productos,
             * por lo que NO podemos conservar el HTML
             * generado originalmente por Liquid.
             */
            this.renderDrawerSizes(
                item
            );

            this.updateDrawerItem(
                item
            );
        }

        buildRecommendationUrl(productUrl) {
            if (!productUrl) {
                return productUrl;
            }

            if (
                this.recommendationContext
                    ?.source !== "nosto"
            ) {
                return productUrl;
            }

            const placementId =
                String(
                    this.recommendationContext
                        ?.placementId ||
                    ""
                ).trim();

            if (!placementId) {
                return productUrl;
            }

            try {
                const url =
                    new URL(
                        productUrl,
                        global.location.origin
                    );

                url.searchParams.set(
                    "nosto",
                    placementId
                );

                return (
                    url.pathname +
                    url.search +
                    url.hash
                );

            } catch (error) {
                return productUrl;
            }
        }


        renderDrawerSizes(item) {
            const product =
                item.activeProduct;

            if (!product) {
                return;
            }

            let container =
                item.querySelector(
                    ".look-product-item__sizes"
                );

            /*
             * Buscar la opción que representa talla.
             */
            const sizeOptionIndex =
                product.options?.findIndex(
                    (option) => {
                        const name =
                            String(
                                option.name ||
                                ""
                            )
                                .trim()
                                .toLowerCase()
                                .normalize("NFD")
                                .replace(
                                    /[\u0300-\u036f]/g,
                                    ""
                                );

                        return (
                            name === "talla" ||
                            name === "size" ||
                            name === "tamano"
                        );
                    }
                ) ?? -1;

            /*
             * Producto sin selector de talla.
             */
            if (sizeOptionIndex < 0) {
                container?.replaceChildren();

                if (container) {
                    container.hidden =
                        true;
                }

                item.selectedVariant =
                    product.variants?.find(
                        (variant) =>
                            variant.available
                    ) ||
                    null;

                return;
            }

            /*
             * Crear contenedor en caso de que el
             * producto original no tuviera talla.
             */
            if (!container) {
                container =
                    document.createElement(
                        "div"
                    );

                container.className =
                    "look-product-item__sizes";

                container.setAttribute(
                    "role",
                    "group"
                );

                container.setAttribute(
                    "aria-label",
                    "Tallas disponibles"
                );

                const productForm =
                    item.querySelector(
                        ".look-product-item__product-form"
                    );

                const content =
                    item.querySelector(
                        ".look-product-item__content"
                    );

                if (productForm) {
                    productForm.before(
                        container
                    );
                } else {
                    content?.appendChild(
                        container
                    );
                }
            }

            container.hidden =
                false;

            container.replaceChildren();

            const option =
                product.options[
                sizeOptionIndex
                ];

            option.values.forEach(
                (sizeValue) => {
                    /*
                     * En este proyecto cada color es otro producto,
                     * así que dentro del producto buscamos
                     * la variante correspondiente a esta talla.
                     */
                    const candidates =
                        product.variants.filter(
                            (variant) =>
                                variant.options?.[
                                sizeOptionIndex
                                ] === sizeValue
                        );

                    const variant =
                        candidates.find(
                            (candidate) =>
                                candidate.available
                        ) ||
                        candidates[0] ||
                        null;

                    if (!variant) {
                        return;
                    }

                    const button =
                        document.createElement(
                            "button"
                        );

                    button.type =
                        "button";

                    button.className =
                        "look-product-item__size";

                    button.dataset.completeLookSize =
                        "";

                    button.dataset.variantId =
                        String(variant.id);

                    button.dataset.available =
                        String(
                            Boolean(
                                variant.available
                            )
                        );

                    button.textContent =
                        sizeValue;

                    if (!variant.available) {
                        button.disabled =
                            true;
                    }

                    const selected =
                        item.selectedVariant &&
                        String(
                            item.selectedVariant.id
                        ) ===
                        String(
                            variant.id
                        );

                    button.classList.toggle(
                        "is-active",
                        selected
                    );

                    button.setAttribute(
                        "aria-pressed",
                        String(selected)
                    );

                    container.appendChild(
                        button
                    );
                }
            );
        }


        selectDrawerSize(button) {
            if (
                button.disabled ||
                button.dataset.available ===
                "false"
            ) {
                return;
            }

            const item =
                button.closest(
                    "[data-nosto-complete-look-item]"
                );

            if (
                !item ||
                !item.activeProduct
            ) {
                return;
            }

            const variant =
                item.activeProduct.variants?.find(
                    (candidate) =>
                        String(candidate.id) ===
                        String(
                            button.dataset.variantId
                        )
                );

            if (
                !variant ||
                !variant.available
            ) {
                return;
            }

            item.selectedVariant =
                variant;

            item.querySelectorAll(
                "[data-complete-look-size]"
            ).forEach(
                (candidate) => {
                    const active =
                        candidate === button;

                    candidate.classList.toggle(
                        "is-active",
                        active
                    );

                    candidate.setAttribute(
                        "aria-pressed",
                        String(active)
                    );
                }
            );

            this.updateDrawerItem(
                item
            );
        }


        updateDrawerItem(item) {
            const product =
                item.activeProduct;

            const variant =
                item.selectedVariant;

            if (!product) {
                return;
            }

            /*
             * Precio mostrado
             */
            const fallbackVariant =
                product.variants?.find(
                    (candidate) =>
                        candidate.available
                ) ||
                product.variants?.[0];

            const displayedVariant =
                variant ||
                fallbackVariant;

            const price =
                item.querySelector(
                    "[data-complete-look-item-price]"
                );

            const compare =
                item.querySelector(
                    "[data-complete-look-item-compare-price]"
                );

            if (
                price &&
                displayedVariant
            ) {
                price.textContent =
                    this.formatMoney(
                        displayedVariant.price
                    );

                const onSale =
                    Number(
                        displayedVariant.compare_at_price
                    ) >
                    Number(
                        displayedVariant.price
                    );

                price.classList.toggle(
                    "is-sale",
                    onSale
                );
            }

            if (
                compare &&
                displayedVariant
            ) {
                const onSale =
                    Number(
                        displayedVariant.compare_at_price
                    ) >
                    Number(
                        displayedVariant.price
                    );

                compare.hidden =
                    !onSale;

                if (onSale) {
                    compare.textContent =
                        this.formatMoney(
                            displayedVariant.compare_at_price
                        );
                }
            }

            /*
             * Imagen específica de variante/talla.
             */
            const image =
                item.querySelector(
                    ".look-product-item__image"
                );

            const variantImage =
                displayedVariant
                    ?.featured_image
                    ?.src;

            if (
                image &&
                variantImage
            ) {
                image.src =
                    variantImage;

                image.removeAttribute(
                    "srcset"
                );
            }

            /*
             * Hidden input utilizado por ProductForm.
             */
            const variantInput =
                item.querySelector(
                    "[data-complete-look-variant-id]"
                );

            if (
                variantInput &&
                variant
            ) {
                variantInput.value =
                    String(
                        variant.id
                    );
            }

            /*
             * Estado general
             */
            if (variant) {
                item.dataset.selectedVariantId =
                    String(
                        variant.id
                    );
            } else {
                delete item.dataset
                    .selectedVariantId;
            }

            const addButton =
                item.querySelector(
                    "[data-complete-look-add-to-cart]"
                );

            if (addButton) {
                addButton.disabled =
                    !variant?.available;

                const text =
                    addButton.querySelector(
                        "[data-add-to-cart-text]"
                    );

                if (text) {
                    text.textContent =
                        variant?.available
                            ? "Agregar a la bolsa"
                            : "Agotado";
                }
            }
        }


        formatMoney(value) {
            if (
                global.theme?.formatMoney
            ) {
                return global.theme.formatMoney(
                    Number(value || 0),

                    global.theme
                        .moneyWithCurrencyFormat ||
                    global.theme
                        .moneyFormat
                );
            }

            return `$${(
                Number(value || 0) /
                100
            ).toFixed(2)}`;
        }

        handleDrawerCancel(event) {
            event.preventDefault();

            this.closeDrawer();
        }

        /**
         * Abre el drawer de "Ver todo".
         */
        async openDrawer(
            triggerButton
        ) {
            const drawer =
                this.querySelector(
                    "[data-complete-look-drawer]"
                );

            if (!drawer) {
                return;
            }

            this.drawerTriggerButton =
                triggerButton;

            this.isDrawerClosing =
                false;

            drawer.classList.remove(
                "is-open",
                "is-closing"
            );

            drawer.removeAttribute(
                "inert"
            );

            if (
                typeof drawer.showModal ===
                "function" &&
                !drawer.open
            ) {
                drawer.showModal();
            } else if (!drawer.open) {
                drawer.setAttribute(
                    "open",
                    ""
                );
            }

            requestAnimationFrame(
                () => {
                    requestAnimationFrame(
                        () => {
                            drawer.classList.add(
                                "is-open"
                            );
                        }
                    );
                }
            );

            document.dispatchEvent(
                new CustomEvent(
                    "theme:scroll:lock",
                    {
                        bubbles: true,

                        detail: {
                            modal:
                                drawer,
                        },
                    }
                )
            );

            if (
                !this.drawerRendered
            ) {
                await this.renderDrawerProducts();
            }
        }

        /**
         * Cierra el drawer manteniendo la animación
         * responsive existente.
         */
        async closeDrawer() {
            const drawer =
                this.querySelector(
                    "[data-complete-look-drawer]"
                );

            if (
                !drawer ||
                !drawer.open ||
                this.isDrawerClosing
            ) {
                return;
            }

            const drawerInner =
                drawer.querySelector(
                    ".nosto-complete-look__drawer-inner"
                );

            this.isDrawerClosing =
                true;

            drawer.classList.add(
                "is-closing"
            );

            const prefersReducedMotion =
                global.matchMedia(
                    "(prefers-reduced-motion: reduce)"
                ).matches;

            const duration =
                prefersReducedMotion
                    ? 1
                    : 350;

            const animations = [];

            const isMobile =
                global.matchMedia(
                    "(max-width: 749px)"
                ).matches;

            const closedTransform =
                isMobile
                    ? "translate3d(0, 100%, 0)"
                    : "translate3d(100%, 0, 0)";

            if (drawerInner) {
                const currentTransform =
                    global.getComputedStyle(
                        drawerInner
                    ).transform;

                const panelAnimation =
                    drawerInner.animate(
                        [
                            {
                                transform:
                                    currentTransform ===
                                        "none"
                                        ? "translate3d(0, 0, 0)"
                                        : currentTransform,
                            },
                            {
                                transform:
                                    closedTransform,
                            },
                        ],
                        {
                            duration,

                            easing:
                                "cubic-bezier(0.4, 0, 0.2, 1)",

                            fill:
                                "forwards",
                        }
                    );

                animations.push(
                    panelAnimation.finished.catch(
                        () => null
                    )
                );
            }

            const currentBackground =
                global.getComputedStyle(
                    drawer
                ).backgroundColor;

            const overlayAnimation =
                drawer.animate(
                    [
                        {
                            backgroundColor:
                                currentBackground,
                        },
                        {
                            backgroundColor:
                                "rgba(0, 0, 0, 0)",
                        },
                    ],
                    {
                        duration,

                        easing:
                            "ease",

                        fill:
                            "forwards",
                    }
                );

            animations.push(
                overlayAnimation.finished.catch(
                    () => null
                )
            );

            await Promise.all(
                animations
            );

            drawer.classList.remove(
                "is-open",
                "is-closing"
            );

            if (
                typeof drawer.close ===
                "function" &&
                drawer.open
            ) {
                drawer.close();
            } else {
                drawer.removeAttribute(
                    "open"
                );
            }

            /*
             * Limpiar fill: forwards para que no afecte
             * la siguiente apertura.
             */
            drawer
                .getAnimations()
                .forEach(
                    (animation) => {
                        animation.cancel();
                    }
                );

            drawerInner
                ?.getAnimations()
                .forEach(
                    (animation) => {
                        animation.cancel();
                    }
                );

            drawer.setAttribute(
                "inert",
                ""
            );

            document.dispatchEvent(
                new CustomEvent(
                    "theme:scroll:unlock",
                    {
                        bubbles: true,
                    }
                )
            );

            this.isDrawerClosing =
                false;

            requestAnimationFrame(
                () => {
                    this.drawerTriggerButton
                        ?.focus?.();

                    this.drawerTriggerButton =
                        null;
                }
            );
        }

        applyDrawerAttribution(item) {
            if (!item) {
                return;
            }

            /*
             * Solo los productos realmente provenientes
             * de Nosto deben llevar attribution.
             */
            if (
                this.recommendationContext
                    ?.source !== "nosto"
            ) {
                return;
            }

            const placementId =
                String(
                    this.recommendationContext
                        ?.placementId ||
                    ""
                ).trim();

            if (!placementId) {
                return;
            }

            item.querySelectorAll(
                "[data-look-item-link]"
            ).forEach(
                (link) => {
                    const href =
                        link.getAttribute(
                            "href"
                        );

                    if (!href) {
                        return;
                    }

                    try {
                        const url =
                            new URL(
                                href,
                                global.location.origin
                            );

                        url.searchParams.set(
                            "nosto",
                            placementId
                        );

                        link.href =
                            url.pathname +
                            url.search +
                            url.hash;

                    } catch (error) {
                        console.warn(
                            "[NostoCompleteLook] No fue posible agregar attribution al link.",
                            error
                        );
                    }
                }
            );
        }

        /**
         * Renderiza todos los productos dentro del drawer.
         *
         * Este flujo sigue utilizando:
         *
         * ?view=nosto-complete-look-item
         *
         * porque es un componente diferente al card compacto
         * mostrado en el carrusel principal.
         */
        async renderDrawerProducts() {
            const container =
                this.querySelector(
                    "[data-complete-look-drawer-products]"
                );

            const loading =
                this.querySelector(
                    "[data-complete-look-drawer-loading]"
                );

            if (
                !container ||
                !this.viewService
            ) {
                return;
            }

            if (loading) {
                loading.hidden =
                    false;
            }

            container.replaceChildren();

            const products =
                this.allRecommendedProducts;

            const promises =
                products.map(
                    async (
                        recommendedProduct
                    ) => {
                        const handle =
                            String(
                                recommendedProduct.handle ||
                                ""
                            ).trim();

                        const productId =
                            String(
                                recommendedProduct.productId ||
                                ""
                            ).trim();

                        if (!handle) {
                            return null;
                        }

                        const placeholder =
                            document.createElement(
                                "div"
                            );

                        placeholder.className =
                            "nosto-complete-look__drawer-item-placeholder";

                        container.appendChild(
                            placeholder
                        );

                        try {
                            const html =
                                await this.viewService.getView({
                                    handle,

                                    productId,

                                    view:
                                        "nosto-complete-look-item",

                                    validationSelector:
                                        "[data-nosto-complete-look-item]",

                                    context: {
                                        source:
                                            "complete-look-drawer",
                                    },
                                });

                            const template =
                                document.createElement(
                                    "template"
                                );

                            template.innerHTML =
                                String(
                                    html
                                ).trim();

                            placeholder.replaceChildren(
                                template.content
                            );

                            const item =
                                placeholder.querySelector(
                                    "[data-nosto-complete-look-item]"
                                );

                            if (item) {
                                this.initializeDrawerItem(
                                    item
                                );
                            }

                            if (item) {
                                this.applyDrawerAttribution(
                                    item
                                );
                            }

                            return placeholder;

                            return placeholder;
                        } catch (error) {
                            console.error(
                                "[NostoCompleteLook] No fue posible cargar un item del drawer.",
                                {
                                    recommendedProduct,
                                    error,
                                }
                            );

                            placeholder.remove();

                            return null;
                        }
                    }
                );

            await Promise.allSettled(
                promises
            );

            if (loading) {
                loading.hidden =
                    true;
            }

            this.drawerRendered =
                true;
        }

        /**
         * Estado vacío.
         */
        showEmptyState() {
            this.dataset.empty =
                "true";

            this.hidden =
                true;
        }

        /**
         * Limpieza de NostoProductCard.
         */
        destroyCards() {
            this.cards.forEach(
                (card) => {
                    try {
                        card.destroy();
                    } catch (error) {
                        console.warn(
                            "[NostoCompleteLook] No fue posible destruir un card.",
                            error
                        );
                    }
                }
            );

            this.cards = [];
        }
    }

    if (
        !customElements.get(
            "nosto-complete-look"
        )
    ) {
        customElements.define(
            "nosto-complete-look",
            NostoCompleteLook
        );
    }
})(window);